import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Collapsible, CollapsibleContent } from './ui/collapsible';
import {
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Sparkles,
  Save
} from 'lucide-react';
import { debounce } from 'lodash';
import productService from '../services/productService';
import SmartField from './SmartField';

// Custom Combobox for category selection
const CategoryCombobox = ({ options, value, onChange, placeholder, error, helperText, required }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const filteredOptions = options.filter(opt =>
    (typeof opt === 'string' ? opt : opt.name || '').toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="relative">
      <Input
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder={placeholder}
        className={error ? 'border-red-500' : ''}
        required={required}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((opt, idx) => {
            const optValue = typeof opt === 'string' ? opt : opt.name || opt;
            return (
              <div
                key={idx}
                className="px-3 py-2 hover:bg-muted cursor-pointer flex items-center justify-between"
                onMouseDown={() => {
                  setInputValue(optValue);
                  onChange(optValue);
                  setIsOpen(false);
                }}
              >
                <span>{optValue}</span>
              </div>
            );
          })}
        </div>
      )}
      {helperText && (
        <p className={`text-xs mt-1 ${error ? 'text-destructive' : 'text-muted-foreground'}`}>{helperText}</p>
      )}
    </div>
  );
};

const InlineProductCreationForm = ({
  isOpen,
  onClose,
  onProductCreated,
  initialData = {},
  clientName = '',
  categories = []
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    product_id: '',
    part_number: '',
    client_ref_number: '',
    category: '',
    specification: '',
    material: '硬质合金',
    coating: '无涂层',
    price: '',
    unit: '支',
    tip_diameter: '',
    total_length: '',
    degree: '',
    shank_diameter: '',
    ...initialData
  });

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationState, setValidationState] = useState({});
  const [suggestions, setSuggestions] = useState({});

  // Auto-focus first field when opened
  const [shouldFocus, setShouldFocus] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldFocus(true);
      // Auto-populate from search context
      if (initialData.searchContext) {
        const { query, category } = initialData.searchContext;
        setFormData(prev => ({
          ...prev,
          name: initialData.name || prev.name,
          category: category || prev.category,
          // Set product_id to the search term content
          product_id: query || prev.product_id
        }));
      } else {
        // If no search context, still populate from initialData
        setFormData(prev => ({
          ...prev,
          name: initialData.name || prev.name,
          product_id: initialData.product_id || prev.product_id,
          specification: initialData.specification || prev.specification,
          category: initialData.category || prev.category,
          unit: initialData.unit || prev.unit,
          price: initialData.price !== undefined ? initialData.price : prev.price
        }));
      }
    }
  }, [isOpen, initialData]);

  // Auto-generate product ID helper
  const generateProductId = (name, category, tipDiameter = '', totalLength = '') => {
    // For raw materials, use tip_diameter and total_length
    if (tipDiameter && totalLength) {
      return `RM-${tipDiameter}-${totalLength}`;
    }
    // Try to parse from specification if available
    const specMatch = name.match(/R(\d+\.?\d*)\*(\d+)/);
    if (specMatch) {
      return `RM-${specMatch[1]}-${specMatch[2]}`;
    }
    const sanitized = name.replace(/[^a-zA-Z0-9\u4e00-\u9fff]/g, '');
    const categoryPrefix = category ? category.slice(0, 2).toUpperCase() : 'PRD';
    const timestamp = Date.now().toString().slice(-6);
    return `${categoryPrefix}-${sanitized.slice(0, 6)}-${timestamp}`;
  };

  // Product ID uniqueness validation (not debounced)
  const validateProductIdInternal = useCallback(async (productId, specification = '', clientRefNumber = '') => {
    if (!productId) {
      setValidationState(prev => ({
        ...prev,
        product_id: { status: 'neutral', message: '' }
      }));
      return;
    }

    try {
      setValidationState(prev => ({
        ...prev,
        product_id: { status: 'checking', message: '检查产品组合...' }
      }));

      // Use the validation endpoint that checks product_id + specification + client_ref_number
      const result = await productService.validateProductId(
        productId,
        specification || formData.specification || '',
        clientRefNumber || formData.client_ref_number || '',
        null // No current ID for new products
      );

      if (!result.isValid) {
        setValidationState(prev => ({
          ...prev,
          product_id: {
            status: 'error',
            message: result.message || '该产品组合已存在',
            details: result.message
          }
        }));
        setErrors(prev => ({ ...prev, product_id: result.message || '该产品组合已存在' }));
      } else {
        setValidationState(prev => ({
          ...prev,
          product_id: { status: 'success', message: '产品组合可用' }
        }));
        setErrors(prev => ({ ...prev, product_id: '' }));
      }
    } catch (error) {
      console.error('Product validation error:', error);
      setValidationState(prev => ({
        ...prev,
        product_id: { status: 'neutral', message: '' }
      }));
    }
  }, [setValidationState, setErrors, formData.specification, formData.client_ref_number]);

  // Debounced product ID, specification, and client_ref_number validation
  const validateProductId = useMemo(
    () => debounce((productId, specification, clientRefNumber) => {
      validateProductIdInternal(productId, specification, clientRefNumber);
    }, 500),
    [validateProductIdInternal]
  );

  // AI Generate product ID
  const aiGenerateProductId = useCallback(async () => {
    if (!formData.name) {
      setErrors(prev => ({ ...prev, product_id: '请先输入产品名称' }));
      return;
    }

    setAiGenerating(true);
    setErrors(prev => ({ ...prev, product_id: '' }));

    try {
      const response = await fetch('/api/ai/generate-product-id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: formData.name,
          product_specification: formData.specification,
          material: formData.material
        })
      });

      const data = await response.json();

      if (data.success && data.product_id) {
        setFormData(prev => ({
          ...prev,
          product_id: data.product_id
        }));
        // Validate the newly generated product ID
        validateProductIdInternal(data.product_id, formData.specification, formData.client_ref_number);
      } else {
        setErrors(prev => ({
          ...prev,
          product_id: data.error || 'AI生成失败，请手动输入'
        }));
      }
    } catch (error) {
      console.error('AI generation error:', error);
      setErrors(prev => ({
        ...prev,
        product_id: 'AI生成失败，请手动输入'
      }));
    } finally {
      setAiGenerating(false);
    }
  }, [formData.name, formData.specification, formData.material, formData.client_ref_number, validateProductIdInternal]);

  // Category suggestion (not debounced)
  const suggestCategoryInternal = useCallback(async (name) => {
    if (!name || name.length < 2) {
      setSuggestions(prev => ({ ...prev, category: [] }));
      return;
    }

    try {
      const response = await productService.searchCategories(name);
      const categoryList = response.categories || [];
      setSuggestions(prev => ({
        ...prev,
        category: categoryList.slice(0, 5)
      }));
    } catch (error) {
      console.error('Category suggestion error:', error);
    }
  }, [setSuggestions]);

  // Debounced category suggestion
  const suggestCategory = useMemo(
    () => debounce((name) => {
      suggestCategoryInternal(name);
    }, 300),
    [suggestCategoryInternal]
  );

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Trigger specific validations
    if (field === 'product_id') {
      validateProductId(value, formData.specification, formData.client_ref_number);
    }

    if (field === 'specification') {
      // Re-validate product_id when specification changes
      if (formData.product_id) {
        validateProductId(formData.product_id, value, formData.client_ref_number);
      }
    }

    if (field === 'client_ref_number') {
      // Re-validate product_id when client_ref_number changes
      if (formData.product_id) {
        validateProductId(formData.product_id, formData.specification, value);
      }
    }

    if (field === 'name') {
      suggestCategory(value);
      // Auto-generate product ID if empty
      if (!formData.product_id) {
        const generatedId = generateProductId(value, formData.category, formData.tip_diameter, formData.total_length);
        setFormData(prev => ({ ...prev, product_id: generatedId }));
        validateProductId(generatedId, formData.specification, formData.client_ref_number);
      }
    }
  };

  // Validation helpers
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name.trim()) {
      newErrors.name = '产品名称不能为空';
    }

    if (!formData.product_id.trim()) {
      newErrors.product_id = '产品编号不能为空';
    } else if (validationState.product_id?.status === 'error') {
      newErrors.product_id = validationState.product_id.message;
    }

    if (!formData.category.trim()) {
      newErrors.category = '产品分类不能为空';
    }

    // Optional field validations
    if (formData.price && isNaN(parseFloat(formData.price))) {
      newErrors.price = '请输入有效的价格';
    }

    if (formData.tip_diameter && isNaN(parseFloat(formData.tip_diameter))) {
      newErrors.tip_diameter = '请输入有效的刃径';
    }

    if (formData.degree && isNaN(parseFloat(formData.degree))) {
      newErrors.degree = '请输入有效的角度';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        source: 'inline_creation',
        created_from: 'quotation',
        client_context: clientName || undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        tip_diameter: formData.tip_diameter ? parseFloat(formData.tip_diameter) : undefined,
        total_length: formData.total_length ? parseFloat(formData.total_length) : undefined,
        degree: formData.degree ? parseFloat(formData.degree) : undefined
      };

      // Remove empty fields
      Object.keys(submitData).forEach(key => {
        if (!submitData[key] && submitData[key] !== 0) {
          delete submitData[key];
        }
      });

      const createdProduct = await productService.createProduct(submitData);

      // Success feedback
      onProductCreated(createdProduct);
      onClose();

    } catch (error) {
      console.error('Product creation error:', error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        // Build error message showing which fields are duplicated
        const parts = [`产品编号 "${formData.product_id}"`];
        if (formData.specification) {
          parts.push(`规格 "${formData.specification}"`);
        }
        if (formData.client_ref_number) {
          parts.push(`物料编码 "${formData.client_ref_number}"`);
        }
        setErrors({ product_id: parts.join(' + ') + ' 的组合已存在' });
      } else {
        setErrors({ submit: error.response?.data?.error || '创建产品失败，请重试' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  const categoryOptions = categories.map(cat => cat.name || cat).concat(suggestions.category || []);

  return (
    <Card
      className="mt-4 border-2 border-blue-500 overflow-hidden"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-primary/10 border-b">
        <div className="flex items-center gap-2">
          <Plus className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-bold text-primary">创建新产品</h3>
          {clientName && (
            <Badge variant="outline" className="text-primary border-primary/30">
              客户: {clientName}
            </Badge>
          )}
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>按 Esc 关闭</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Form Content */}
      <CardContent className="p-6">
        {/* Essential Fields */}
        <h4 className="text-sm font-bold mb-4">基本信息 *</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Product Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium">产品名称 *</label>
            <div className="relative">
              <Input
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
                autoFocus={shouldFocus}
              />
              {formData.name && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>自动生成编号</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className={`text-xs ${errors.name ? 'text-destructive' : 'text-muted-foreground'}`}>
              {errors.name || '请输入具体的产品名称'}
            </p>
          </div>

          {/* Product ID */}
          <div className="space-y-2">
            <label className="text-sm font-medium">产品编号 *</label>
            <div className="relative flex gap-2">
              <Input
                value={formData.product_id}
                onChange={(e) => handleFieldChange('product_id', e.target.value)}
                className={errors.product_id ? 'border-red-500' : ''}
                placeholder="点击右侧按钮AI生成"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={aiGenerateProductId}
                disabled={aiGenerating || !formData.name}
                className="shrink-0"
              >
                {aiGenerating ? (
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 text-purple-500" />
                )}
              </Button>
              {validationState.product_id?.status && (
                <span className="absolute right-[60px] top-1/2 -translate-y-1/2">
                  {validationState.product_id.status === 'checking' && (
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                  {validationState.product_id.status === 'success' && (
                    <CheckCircle className="h-4 w-4 text-success" />
                  )}
                  {validationState.product_id.status === 'error' && (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                </span>
              )}
            </div>
            <p className={`text-xs ${
              validationState.product_id?.status === 'success' ? 'text-success' :
              validationState.product_id?.status === 'error' ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {validationState.product_id?.message || errors.product_id || '点击星号按钮使用AI生成，或手动输入'}
            </p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {/* Part Number */}
          <div className="space-y-2">
            <label className="text-sm font-medium">零件号 (Part Number)</label>
            <Input
              value={formData.part_number}
              onChange={(e) => handleFieldChange('part_number', e.target.value)}
              placeholder="如：FQT-001，HSS-6MM等"
            />
            <p className="text-xs text-muted-foreground">可选：制造商零件号或客户参考号</p>
          </div>

          {/* Client Reference Number */}
          <div className="space-y-2">
            <label className="text-sm font-medium">物料编码</label>
            <Input
              value={formData.client_ref_number}
              onChange={(e) => handleFieldChange('client_ref_number', e.target.value)}
              placeholder="请输入物料编码（可选）"
            />
            <p className="text-xs text-muted-foreground">可选：客户或内部物料编码</p>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="text-sm font-medium">产品分类 *</label>
            <CategoryCombobox
              options={categoryOptions}
              value={formData.category}
              onChange={(value) => handleFieldChange('category', value)}
              placeholder="选择或输入产品分类"
              error={!!errors.category}
              helperText={errors.category || '选择或输入产品分类'}
              required
            />
          </div>
        </div>

        {/* Basic Specification */}
        <div className="space-y-2 mb-6">
          <label className="text-sm font-medium">产品规格</label>
          <Textarea
            value={formData.specification}
            onChange={(e) => handleFieldChange('specification', e.target.value)}
            rows={2}
            placeholder="请描述产品的主要规格参数..."
          />
          <p className="text-xs text-muted-foreground">可选：产品的详细规格说明</p>
        </div>

        {/* Advanced Fields Toggle */}
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="mb-4"
          >
            {showAdvanced ? '收起' : '展开'} 详细参数
            {showAdvanced ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
            <Badge variant="secondary" className="ml-2">
              {Object.keys(formData).filter(key =>
                !['name', 'product_id', 'part_number', 'category', 'specification'].includes(key) && formData[key]
              ).length}
            </Badge>
          </Button>

          <Collapsible open={showAdvanced}>
            <CollapsibleContent>
              <Separator className="mb-4" />

              <h4 className="text-sm font-bold mb-4">详细参数</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Material */}
                <SmartField
                  name="material"
                  label="材料"
                  type="select"
                  value={formData.material}
                  onChange={(name, value) => handleFieldChange(name, value)}
                  category={formData.category}
                />

                {/* Coating */}
                <SmartField
                  name="coating"
                  label="涂层"
                  type="select"
                  value={formData.coating}
                  onChange={(name, value) => handleFieldChange(name, value)}
                  category={formData.category}
                />

                {/* Tip Diameter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">刃径 (mm)</label>
                  <Input
                    type="number"
                    value={formData.tip_diameter}
                    onChange={(e) => handleFieldChange('tip_diameter', e.target.value)}
                    step="0.1"
                    min="0"
                    className={errors.tip_diameter ? 'border-red-500' : ''}
                  />
                  {errors.tip_diameter && (
                    <p className="text-xs text-destructive">{errors.tip_diameter}</p>
                  )}
                </div>

                {/* Total Length */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">总长度 (mm)</label>
                  <Input
                    type="number"
                    value={formData.total_length}
                    onChange={(e) => handleFieldChange('total_length', e.target.value)}
                    step="0.1"
                    min="0"
                  />
                </div>

                {/* Degree */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">角度 (°)</label>
                  <Input
                    type="number"
                    value={formData.degree}
                    onChange={(e) => handleFieldChange('degree', e.target.value)}
                    step="0.5"
                    min="0"
                    max="360"
                    className={errors.degree ? 'border-red-500' : ''}
                  />
                  {errors.degree && (
                    <p className="text-xs text-destructive">{errors.degree}</p>
                  )}
                </div>

                {/* Shank Diameter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">柄径 (mm)</label>
                  <Input
                    type="number"
                    value={formData.shank_diameter}
                    onChange={(e) => handleFieldChange('shank_diameter', e.target.value)}
                    step="0.1"
                    min="0"
                  />
                </div>

                {/* Unit */}
                <SmartField
                  name="unit"
                  label="单位"
                  type="select"
                  value={formData.unit}
                  onChange={(name, value) => handleFieldChange(name, value)}
                  category={formData.category}
                />
              </div>

              {/* Price */}
              <div className="space-y-2 mb-4">
                <label className="text-sm font-medium">参考价格</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">¥</span>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleFieldChange('price', e.target.value)}
                    step="0.01"
                    min="0"
                    className={`pl-8 ${errors.price ? 'border-red-500' : ''}`}
                  />
                </div>
                <p className={`text-xs ${errors.price ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {errors.price || '可选：用于报价参考'}
                </p>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{errors.submit}</AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-xs text-muted-foreground">按 Ctrl+Enter 快速提交</span>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>取消</Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || validationState.product_id?.status === 'error'}
            >
              {isSubmitting ? (
                '创建中...'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  创建产品
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InlineProductCreationForm;
